import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, RotateCcw } from "lucide-react";

/**
 * Session Contract card (iter133 Phase 4A).
 *
 * Design constraints (user-specified after Phase 3C):
 *   1. NO pre-filled defaults. A limit the user didn't pick isn't
 *      a self-limit. Placeholder text guides, but doesn't fill.
 *   2. Reference line beside each numeric input, drawn from the
 *      user's OWN last-30d data. Silently omitted (null) when the
 *      user hasn't accumulated enough history — never zero, never
 *      "n/a".
 *   3. Qualitative field ("what would make me stop trading today?")
 *      saved verbatim. Not machine-checkable — reflected back later.
 *   4. "Carry forward yesterday's" pre-fills from the most recent
 *      prior contract so the user edits only what changed.
 *
 * Free-tier surface — deterministic user data + user's own history.
 * The Radar feed (Elite-gated) will consume this in Phase 5.
 */
export default function SessionContractCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);     // today's, if any
  const [refs, setRefs] = useState(null);
  const [latest, setLatest] = useState(null);          // prior day, if any

  // Form state — all strings so empty state stays empty (no zero-fill).
  const [maxSize, setMaxSize] = useState("");   // stored notional (legacy fallback)
  const [maxLev, setMaxLev] = useState("");
  const [maxPos, setMaxPos] = useState("");
  const [stopCond, setStopCond] = useState("");
  // Fix 3 — lot-size expression of the size cap. Stored notional = lots × price.
  const [sizeSymbol, setSizeSymbol] = useState("");
  const [sizeLots, setSizeLots] = useState("");
  const [sizePrice, setSizePrice] = useState("");
  const [sizeMultiplier, setSizeMultiplier] = useState(null);

  const fetchMultiplier = async (sym) => {
    const s = (sym || "").trim().toUpperCase();
    if (!s) { setSizeMultiplier(null); return; }
    try {
      const { data } = await api.get("/coach/contract-spec", { params: { symbol: s } });
      setSizeMultiplier(data?.contract_multiplier ?? null);
    } catch { setSizeMultiplier(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: today }, { data: prev }] = await Promise.all([
        api.get("/coach/session-contract/today"),
        api.get("/coach/session-contract/latest"),
      ]);
      setContract(today?.contract || null);
      setRefs(today?.references || null);
      setLatest(prev?.contract || null);
      if (today?.contract) {
        // Prefill from today's already-committed contract — editable.
        setMaxSize(String(today.contract.max_position_size_notional ?? ""));
        setMaxLev(String(today.contract.max_leverage ?? ""));
        setMaxPos(String(today.contract.max_positions_open ?? ""));
        setStopCond(today.contract.stop_trading_condition || "");
        setSizeSymbol(today.contract.max_position_size_symbol || "");
        setSizeLots(today.contract.max_position_size_lots != null ? String(today.contract.max_position_size_lots) : "");
        setSizePrice(today.contract.max_position_size_ref_price != null ? String(today.contract.max_position_size_ref_price) : "");
        if (today.contract.max_position_size_symbol) fetchMultiplier(today.contract.max_position_size_symbol);
      }
    } catch (err) {
      toast.error("Couldn't load session contract");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const carryForward = () => {
    if (!latest) return;
    setMaxSize(String(latest.max_position_size_notional ?? ""));
    setMaxLev(String(latest.max_leverage ?? ""));
    setMaxPos(String(latest.max_positions_open ?? ""));
    setStopCond(latest.stop_trading_condition || "");
    setSizeSymbol(latest.max_position_size_symbol || "");
    setSizeLots(latest.max_position_size_lots != null ? String(latest.max_position_size_lots) : "");
    setSizePrice(latest.max_position_size_ref_price != null ? String(latest.max_position_size_ref_price) : "");
    if (latest.max_position_size_symbol) fetchMultiplier(latest.max_position_size_symbol);
    toast.success("Prefilled from your last session — edit what changed");
  };

  // Fix 3 — derive the stored notional (lots × price). Falls back to the
  // legacy stored value when editing an older contract that had no lot data.
  const lotsNum = parseFloat(sizeLots);
  const priceNum = parseFloat(sizePrice);
  const computedNotional =
    (Number.isFinite(lotsNum) && lotsNum > 0 && Number.isFinite(priceNum) && priceNum > 0)
      ? lotsNum * priceNum : null;
  const legacyNotional = parseFloat(maxSize);
  const effectiveNotional =
    computedNotional != null ? computedNotional
      : (Number.isFinite(legacyNotional) && legacyNotional > 0 ? legacyNotional : null);
  const trueUsdNotional =
    (computedNotional != null && Number.isFinite(sizeMultiplier) && sizeMultiplier > 0)
      ? computedNotional * sizeMultiplier : null;
  const fmtNum = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        max_position_size_notional: effectiveNotional,
        max_leverage: parseFloat(maxLev),
        max_positions_open: parseInt(maxPos, 10),
        stop_trading_condition: stopCond,
      };
      if (computedNotional != null) {
        body.max_position_size_lots = lotsNum;
        body.max_position_size_ref_price = priceNum;
        if (sizeSymbol.trim()) body.max_position_size_symbol = sizeSymbol.trim().toUpperCase();
      }
      const { data } = await api.post("/coach/session-contract", body);
      setContract(data.contract);
      toast.success("Session contract saved for today");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail)
                  || "Couldn't save session contract";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // iter134 D5a follow-up — Redo Day Zero from Settings. Small UX gap
  // closer: hints show here, so the entry point to update them should
  // live here too. Resets the profile server-side then navigates to
  // /coach where the walkthrough resumes at step 0.
  const redoDayZero = async () => {
    try {
      await api.post("/coach/day-zero/reset");
      toast.success("Day Zero reset — heading to Coach to walk through it again");
      navigate("/coach");
    } catch {
      toast.error("Couldn't reset Day Zero — try again");
    }
  };

  // Render helpers. Reference line ONLY when the value is a positive
  // number — silent omission enforces user constraint #2.
  const refLine = (value, formatter) =>
    (typeof value === "number" && value > 0)
      ? (
        <p className="text-xs text-[var(--bt-text-muted)]"
           data-testid="session-contract-ref-line">
          Your last-30d median: <span className="text-[var(--bt-accent)] font-mono">{formatter(value)}</span>
        </p>
      )
      : null;

  // iter134 D5a — Session Contract Hint. Surfaces the
  // Day-Zero-derived reference number BESIDE each input (never as
  // a pre-fill — respects user constraint #1). Only rendered when
  // the profile is complete AND the value is a positive number
  // (same silent-omission rule as history-based reference lines).
  // Tooltip explains provenance so users know it's from their own
  // Day Zero answers, not the market or an algorithm.
  const profile = refs?.profile_reference;
  const profileHint = (value, formatter, testid) =>
    (profile && typeof value === "number" && value > 0)
      ? (
        <p className="text-xs text-[var(--bt-text-muted)]"
           title="From your Day Zero setup — not a limit, just a hint. Redo on /coach."
           data-testid={testid}>
          Day Zero hint: <span className="text-[var(--bt-accent)] font-mono">{formatter(value)}</span>
        </p>
      )
      : null;

  const canSubmit = useMemo(() => {
    const ml = parseFloat(maxLev);
    const mpo = parseInt(maxPos, 10);
    return !saving
        && Number.isFinite(effectiveNotional) && effectiveNotional > 0
        && Number.isFinite(ml) && ml > 0
        && Number.isFinite(mpo) && mpo > 0
        && stopCond.trim().length > 0;
  }, [effectiveNotional, maxLev, maxPos, stopCond, saving]);

  return (
    <form
      onSubmit={onSubmit}
      className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-5 lg:col-span-2"
      data-testid="session-contract-card"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-sm border border-[var(--bt-border)] flex items-center justify-center">
          <ClipboardCheck className="w-4 h-4 text-[var(--bt-accent)]" />
        </span>
        <div className="flex-1">
          <p className="bt-label">AI Coach</p>
          <h2 className="font-heading text-xl">Today's Session Contract</h2>
        </div>
        {latest && !contract && (
          <Button
            type="button"
            variant="outline"
            onClick={carryForward}
            data-testid="session-contract-carry-forward-btn"
            className="border-[var(--bt-border)] hover:bg-[var(--bt-surface-hover)] rounded-sm h-9 text-xs"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Carry forward yesterday's
          </Button>
        )}
      </div>

      <p className="text-sm text-[var(--bt-text-muted)]">
        Set your own limits for today. Coach will nudge you when a live
        order breaks one of these. Yours to define — no defaults on purpose.
      </p>

      {loading ? (
        <div className="py-8 flex items-center justify-center text-sm text-[var(--bt-text-muted)]">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Fix 3 — Max position size via LOT SIZE. Stored cap = lots × price
              (same unit the Size Anomaly Rule compares). Contract multiplier
              shown only as a friendly true-USD helper, never stored. */}
          <div className="space-y-3 rounded-sm border border-[var(--bt-border)] p-4">
            <Label className="bt-label">Max position size (by lot size)</Label>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--bt-text-muted)]">Symbol (optional)</Label>
                <Input
                  type="text"
                  value={sizeSymbol}
                  onChange={(e) => setSizeSymbol(e.target.value.toUpperCase())}
                  onBlur={(e) => fetchMultiplier(e.target.value)}
                  placeholder="e.g. BTCUSD"
                  data-testid="session-contract-size-symbol-input"
                  className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm bt-num uppercase"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--bt-text-muted)]">Lot size (contracts)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={sizeLots}
                  onChange={(e) => setSizeLots(e.target.value)}
                  placeholder="e.g. 100"
                  data-testid="session-contract-max-size-input"
                  className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm bt-num"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--bt-text-muted)]">Reference price</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={sizePrice}
                  onChange={(e) => setSizePrice(e.target.value)}
                  placeholder="e.g. 60000"
                  data-testid="session-contract-size-price-input"
                  className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm bt-num"
                />
              </div>
            </div>
            {computedNotional != null ? (
              <div className="space-y-0.5">
                <p className="text-sm" data-testid="session-contract-computed-notional">
                  Cap stored (notional): <span className="text-[var(--bt-accent)] font-mono font-semibold">{fmtNum(computedNotional)}</span>
                  <span className="text-xs text-[var(--bt-text-muted)]"> = {fmtNum(lotsNum)} × {fmtNum(priceNum)}</span>
                </p>
                {trueUsdNotional != null && (
                  <p className="text-xs text-[var(--bt-text-muted)]"
                     title="True USD notional using this contract's Delta multiplier. Shown for intuition only — the cap Coach checks against uses lots × price."
                     data-testid="session-contract-true-usd-helper">
                    ≈ true USD notional: <span className="font-mono">${fmtNum(trueUsdNotional)}</span>
                    <span className="opacity-70"> ({fmtNum(lotsNum)} × {sizeMultiplier} × {fmtNum(priceNum)})</span>
                  </p>
                )}
              </div>
            ) : (
              (Number.isFinite(legacyNotional) && legacyNotional > 0) ? (
                <p className="text-xs text-[var(--bt-text-muted)]" data-testid="session-contract-legacy-notional">
                  Current stored cap: <span className="text-[var(--bt-accent)] font-mono">{fmtNum(legacyNotional)}</span> — enter lot size & reference price to change it.
                </p>
              ) : (
                <p className="text-xs text-[var(--bt-text-muted)]">Enter lot size and a reference price to set your notional cap.</p>
              )
            )}
            {refLine(refs?.median_position_size_notional, (v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 }))}
            {profileHint(profile?.max_position_size_notional, (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 }), "session-contract-profile-hint-size")}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Max leverage */}
            <div className="space-y-2">
              <Label className="bt-label">Max leverage</Label>
              <Input
                type="number" step="0.5" min="0"
                value={maxLev}
                onChange={(e) => setMaxLev(e.target.value)}
                placeholder="e.g. 10"
                data-testid="session-contract-max-lev-input"
                className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm bt-num"
              />
              {refLine(refs?.median_leverage, (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}×`)}
              {profileHint(profile?.max_leverage, (v) => `${v}×`, "session-contract-profile-hint-lev")}
            </div>

            {/* Max concurrent open positions */}
            <div className="space-y-2">
              <Label className="bt-label">Max concurrent open positions</Label>
              <Input
                type="number" step="1" min="0"
                value={maxPos}
                onChange={(e) => setMaxPos(e.target.value)}
                placeholder="e.g. 3"
                data-testid="session-contract-max-pos-input"
                className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm bt-num"
              />
              {refLine(refs?.median_positions_open, (v) => `${Math.round(v)}`)}
              {profileHint(profile?.max_positions_open, (v) => `${v}`, "session-contract-profile-hint-pos")}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="bt-label">
              What would make you stop trading today?
            </Label>
            <textarea
              rows={2}
              maxLength={500}
              value={stopCond}
              onChange={(e) => setStopCond(e.target.value)}
              placeholder="e.g. If I take 3 back-to-back losses, or I catch myself averaging down"
              data-testid="session-contract-stop-cond-input"
              className="w-full bg-transparent border border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] rounded-sm p-3 text-sm resize-y"
            />
            <p className="text-xs text-[var(--bt-text-muted)]">
              One sentence, in your words. Coach will reflect this back
              at you later — most powerful when it's specific.
            </p>
            {profile?.stop_trading_condition_hint && (
              <p className="text-xs text-[var(--bt-text-muted)]"
                 title="From your Day Zero setup — not a limit, just a hint. Redo on /coach."
                 data-testid="session-contract-profile-hint-stop">
                Day Zero hint: <span className="text-[var(--bt-accent)] italic">"{profile.stop_trading_condition_hint}"</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-[var(--bt-border)]">
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="session-contract-submit-btn"
              className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-11"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
              ) : (
                <><ClipboardCheck className="w-4 h-4 mr-1.5" /> {contract ? "Update today's contract" : "Set today's contract"}</>
              )}
            </Button>
            {contract && (
              <span className="text-xs text-[var(--bt-text-muted)]"
                    data-testid="session-contract-status">
                Committed for {contract.date_utc}
              </span>
            )}
            {profile && (
              <button
                type="button"
                onClick={redoDayZero}
                data-testid="session-contract-redo-day-zero-btn"
                className="ml-auto text-xs text-[var(--bt-text-muted)] hover:text-[var(--bt-accent)] flex items-center gap-1 transition-colors"
                title="Reset your Day Zero answers and re-walk the questionnaire on Coach"
              >
                <RotateCcw className="w-3 h-3" /> Redo Day Zero
              </button>
            )}
          </div>
        </>
      )}
    </form>
  );
}
